const DANGEROUS_TAGS = new Set([
  'script',
  'style',
  'iframe',
  'object',
  'embed',
  'link',
  'meta',
  'base',
  'form',
  'input',
  'button',
  'textarea',
  'select'
]);

const SAFE_URL = /^(https?:|mailto:|tel:|\/|#)/i;

const hasHtmlTag = (value: string) => /<\s*[a-z][\s\S]*>/i.test(value);

function sanitizeHref(value: string) {
  const href = (value || '').trim();
  return SAFE_URL.test(href) ? href : '#';
}

function sanitizeSrc(value: string) {
  const src = (value || '').trim();
  return /^(https?:|\/|data:image\/)/i.test(src) ? src : '';
}

export function sanitizeRichHtml(input?: string) {
  const raw = (input || '').trim();
  if (!raw) {
    return '';
  }

  if (!hasHtmlTag(raw)) {
    return raw
      .split(/\n+/)
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => `<p>${line}</p>`)
      .join('');
  }

  if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
    return raw;
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${raw}</div>`, 'text/html');
  const root = doc.body.firstElementChild as HTMLElement | null;
  if (!root) {
    return '';
  }

  root.querySelectorAll('*').forEach((node) => {
    const tag = node.tagName.toLowerCase();
    if (DANGEROUS_TAGS.has(tag)) {
      node.remove();
      return;
    }

    [...node.attributes].forEach((attr) => {
      const name = attr.name.toLowerCase();
      if (name.startsWith('on')) {
        node.removeAttribute(attr.name);
        return;
      }
      if (name === 'href') {
        node.setAttribute('href', sanitizeHref(attr.value));
        node.setAttribute('target', '_blank');
        node.setAttribute('rel', 'noreferrer noopener');
        return;
      }
      if (name === 'src') {
        const safeSrc = sanitizeSrc(attr.value);
        if (!safeSrc) {
          node.remove();
          return;
        }
        node.setAttribute('src', safeSrc);
        return;
      }
      if (!['style', 'class', 'alt', 'title', 'align'].includes(name)) {
        node.removeAttribute(attr.name);
      }
    });
  });

  return root.innerHTML;
}

export function stripRichText(input?: string) {
  const raw = (input || '').trim();
  if (!raw) {
    return '';
  }
  if (!hasHtmlTag(raw)) {
    return raw.replace(/\s+/g, ' ').trim();
  }
  if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
    return raw.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  }
  const parser = new DOMParser();
  const doc = parser.parseFromString(raw, 'text/html');
  return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
}

